import { Component, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CarouselModule } from 'primeng/carousel';
import { CheckboxModule } from 'primeng/checkbox';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { ImageModule } from 'primeng/image';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { Tabs, Tab, TabList, TabPanel, TabPanels } from 'primeng/tabs';
import { catchError, finalize, of, tap } from 'rxjs';
import { AnalyticsService } from '../../core/analytics.service';
import { BrandService } from '../../core/brand.service';
import { getControlError, shouldShowError } from '../../core/form-validation.utils';
import {
  normalizeSpanishMobilePhone,
  optionalSpanishMobileValidator,
} from '../../core/optional-spanish-mobile.validator';
import { AuthApiService } from '../../core/auth-api.service';
import { ApiErrorResponse } from '../../core/auth.models';

type RegisterTab = 'tenant' | 'admin';

@Component({
  selector: 'app-register',
  imports: [
    ButtonModule,
    CardModule,
    CarouselModule,
    CheckboxModule,
    FloatLabelModule,
    IconFieldModule,
    ImageModule,
    InputIconModule,
    InputTextModule,
    DialogModule,
    MessageModule,
    ReactiveFormsModule,
    RouterLink,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
  ],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent {
  private readonly title = inject(Title);
  private readonly authApi = inject(AuthApiService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly analytics = inject(AnalyticsService);
  private readonly brand = inject(BrandService);

  public activeTab: RegisterTab = 'tenant';
  public tenantForm: FormGroup = this.initTenantForm();
  public adminForm: FormGroup = this.initAdminForm();
  public tenantConditions = new FormControl(false, Validators.requiredTrue);
  public adminConditions = new FormControl(false, Validators.requiredTrue);

  public tenantError = '';
  public tenantSuccess = false;
  public adminError = '';
  public adminSuccess = false;
  public tenantLoading = false;
  public adminLoading = false;
  public showTenantPhoneReminderDialog = false;

  public readonly privacyPolicyUrl = this.brand.brand.footer.privacyPolicyUrl;
  public readonly legalNoticeUrl = this.brand.brand.footer.legalNoticeUrl;
  public readonly supportEmail = this.brand.brand.footer.customerSupportEmail;
  public readonly supportPhone = this.brand.brand.footer.customerSupportPhoneDisplay;

  public readonly shouldShowError = shouldShowError;
  public readonly getControlError = getControlError;

  public images = [
    { src: 'assets/img/explicacionAgua1.png', title: 'Datos del agua' },
    { src: 'assets/img/explicacionAgua2.png', title: 'Detalles agua 2' },
    { src: 'assets/img/explicacionCalef.png', title: 'Datos de calefacción' },
  ];

  constructor() {
    this.title.setTitle(this.brand.buildPageTitle('Registro'));
  }

  public submitTenant(): void {
    this.tenantError = '';
    this.tenantSuccess = false;

    if (this.tenantConditions.invalid) {
      this.tenantConditions.markAsTouched();
      this.tenantError = 'Debes aceptar el aviso legal y la política de privacidad.';
      return;
    }
    if (this.tenantForm.invalid) {
      this.tenantForm.markAllAsTouched();
      return;
    }

    const value = this.tenantForm.value;
    const phone = normalizeSpanishMobilePhone(value.phone);
    if (!phone) {
      this.showTenantPhoneReminderDialog = true;
      return;
    }

    this.executeTenantRegister(Number(value.liquidationAmount), phone);
  }

  public cancelTenantPhoneReminder(): void {
    this.showTenantPhoneReminderDialog = false;
  }

  public continueTenantWithoutPhone(): void {
    this.showTenantPhoneReminderDialog = false;
    const liquidationAmount = Number(this.tenantForm.value.liquidationAmount);
    this.executeTenantRegister(liquidationAmount);
  }

  private executeTenantRegister(liquidationAmount: number, phone?: string): void {
    this.tenantLoading = true;
    const value = this.tenantForm.value;

    this.authApi
      .registerTenant({
        email: value.email,
        liquidationCode: value.liquidationCode,
        liquidationAmount,
        ...(phone ? { phone } : {}),
      })
      .pipe(
        tap(() => {
          this.tenantSuccess = true;
          this.analytics.trackEvent('register_tenant_success');
          this.tenantForm.reset();
          this.tenantConditions.reset(false);
        }),
        catchError((err: { error?: ApiErrorResponse }) => {
          this.tenantError =
            err?.error?.error?.message ??
            err?.error?.message ??
            'No se pudo completar el registro. Verifica los datos e inténtalo de nuevo.';
          return of(null);
        }),
        finalize(() => {
          this.tenantLoading = false;
        })
      )
      .subscribe();
  }

  public submitAdminRequest(): void {
    this.adminError = '';
    this.adminSuccess = false;

    if (this.adminConditions.invalid) {
      this.adminConditions.markAsTouched();
      this.adminError = 'Debes aceptar el aviso legal y la política de privacidad.';
      return;
    }
    if (this.adminForm.invalid) {
      this.adminForm.markAllAsTouched();
      return;
    }

    this.adminLoading = true;
    const value = this.adminForm.value;

    this.authApi
      .requestAdminRegistration({
        email: value.email,
        phone: value.phone,
        acceptedPrivacyPolicy: true,
        appOrigin: this.brand.brand.appOrigin,
      })
      .pipe(
        tap(() => {
          this.adminSuccess = true;
          this.analytics.trackEvent('register_admin_success');
          this.adminForm.reset();
          this.adminConditions.reset(false);
        }),
        catchError((err: { error?: ApiErrorResponse }) => {
          this.adminError =
            err?.error?.error?.message ??
            err?.error?.message ??
            'No se pudo enviar la solicitud. Inténtalo de nuevo o contacta con atención al cliente.';
          return of(null);
        }),
        finalize(() => {
          this.adminLoading = false;
        })
      )
      .subscribe();
  }

  private initTenantForm(): FormGroup {
    return this.formBuilder.group({
      email: [null, [Validators.required, Validators.email]],
      phone: [null, optionalSpanishMobileValidator()],
      liquidationCode: [null, Validators.required],
      liquidationAmount: [null, [Validators.required, Validators.min(0)]],
    });
  }

  private initAdminForm(): FormGroup {
    return this.formBuilder.group({
      email: [null, [Validators.required, Validators.email]],
      phone: [null, [Validators.required, Validators.minLength(9)]],
    });
  }
}
